import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Mail, ChevronRight, Package, FileText, Clock, Route, Download } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { canSeeDashboard } from "../config/features";

const STATUS_STYLE = {
  draft: "bg-zinc-700/60 text-zinc-300",
  sent: "bg-blue-500/15 text-blue-300",
  acknowledged: "bg-emerald-500/15 text-emerald-400",
};
export function StatusChip({ status }) {
  const { t } = useLang();
  const s = status || "sent";
  const label = { draft: t("Draft"), sent: t("Sent"), acknowledged: t("Acknowledged") }[s] || s;
  return <span data-testid={`status-chip-${s}`} className={`shrink-0 inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[s] || STATUS_STYLE.sent}`}>{label}</span>;
}

function DashboardSummary() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [d, setD] = useState(null);
  const show = canSeeDashboard(user);
  useEffect(() => { if (show) api.get("/pro/dashboard", { params: { range: "week" } }).then(({ data }) => setD(data)).catch(() => {}); }, [show]);
  if (!show || !d) return null;
  const items = [
    { label: t("Reports sent"), value: d.reports_sent },
    { label: t("Hours logged"), value: d.hours_logged },
    { label: t("Km driven"), value: d.km_driven },
    { label: t("Parts used"), value: d.parts_used },
  ];
  return (
    <button onClick={() => nav("/dashboard")} data-testid="reports-dashboard-summary"
      className="w-full text-left bg-[#18181B] border border-zinc-800 hover:border-[#F26722]/50 rounded-2xl p-4 mb-4 transition-colors">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold tracking-wider text-[#F26722] uppercase">{t("This week so far")}</span>
        <span className="text-xs text-zinc-400 flex items-center gap-1">{t("View full dashboard")} <ChevronRight size={14} /></span>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-3">
        {items.map((it) => (
          <div key={it.label}><p className="text-xl font-extrabold text-white">{it.value ?? "—"}</p><p className="text-[10px] text-zinc-500 leading-tight mt-0.5">{it.label}</p></div>
        ))}
      </div>
    </button>
  );
}

function partsCount(r) {
  let n = 0;
  for (const m of r.machines || []) for (const p of m.parts || []) n += Number(p.quantity || 1);
  return n;
}

function startOfToday() { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }
function startOfWeek() { const d = startOfToday(); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); return d; }
function startOfMonth() { const d = startOfToday(); d.setDate(1); return d; }
function dateFloor(preset) {
  if (preset === "today") return startOfToday();
  if (preset === "week") return startOfWeek();
  if (preset === "month") return startOfMonth();
  return null;
}

function toCsv(rows, t) {
  const head = ["Customer", "Call ID", "Date", "Status", "Machines", "Parts", "Recipient"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [head.join(",")];
  for (const r of rows) {
    lines.push([r.customer_name, r.call_id, r.report_date || (r.created_at || "").slice(0, 10), r.status || "sent", (r.machines || []).length, partsCount(r), r.recipient_name || ""].map(esc).join(","));
  }
  return lines.join("\n");
}

export default function Reports() {
  const nav = useNavigate();
  const { t } = useLang();
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/reports").then(({ data }) => setAll(data)).finally(() => setLoading(false));
  }, []);

  const term = q.trim().toLowerCase();
  const floor = dateFloor(datePreset);
  const reports = all.filter((r) => {
    if (status !== "all" && (r.status || "sent") !== status) return false;
    if (floor && new Date(r.created_at || r.report_date || 0) < floor) return false;
    if (term && ![r.customer_name, r.call_id, r.report_date, r.mechanic_name].some((v) => (v || "").toLowerCase().includes(term))) return false;
    return true;
  });

  const exportCsv = () => {
    const csv = toCsv(reports, t);
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `reports-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const StatusBtn = ({ v, label }) => (
    <button onClick={() => setStatus(v)} data-testid={`status-filter-${v}`}
      className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${status === v ? "bg-[#F26722] text-white" : "bg-[#18181B] border border-zinc-800 text-zinc-400 hover:text-white"}`}>{label}</button>
  );
  const DateBtn = ({ v, label }) => (
    <button onClick={() => setDatePreset(v)} data-testid={`date-filter-${v}`}
      className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${datePreset === v ? "bg-[#F26722] text-white" : "bg-[#18181B] border border-zinc-800 text-zinc-400 hover:text-white"}`}>{label}</button>
  );

  return (
    <>
      <PageHeader title={t("Reports")} subtitle={t("Service reports sent to the office")}
        right={<button onClick={exportCsv} data-testid="export-csv-btn" className="bg-[#18181B] border border-zinc-800 text-zinc-200 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5 hover:border-zinc-700"><Download size={16} /> {t("Export CSV")}</button>} />
      <div className="px-4 pt-4">
        <DashboardSummary />
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="reports-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search customer, call ID, date...")}
            className="w-full bg-[#141416] border border-zinc-800 text-white rounded-xl pl-11 pr-4 py-3 placeholder-zinc-500 focus:border-[#F26722] focus:outline-none" />
        </div>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="status-filters">
          <StatusBtn v="all" label={t("All")} /><StatusBtn v="draft" label={t("Draft")} /><StatusBtn v="sent" label={t("Sent")} /><StatusBtn v="acknowledged" label={t("Acknowledged")} />
        </div>
        <div className="flex flex-wrap gap-2 mt-2" data-testid="date-filters">
          <DateBtn v="all" label={t("All")} /><DateBtn v="today" label={t("Today")} /><DateBtn v="week" label={t("This week")} /><DateBtn v="month" label={t("This month")} />
        </div>
        <div className="mt-4 space-y-3 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4">
          {loading && <p className="text-zinc-500 text-center py-10 md:col-span-full">{t("Loading...")}</p>}
          {!loading && reports.length === 0 && <p className="text-zinc-500 text-center py-10 md:col-span-full" data-testid="reports-empty">{t("No reports found.")}</p>}
          {reports.map((r) => {
            const pc = partsCount(r); const mc = (r.machines || []).length;
            return (
              <div key={r.id} data-testid={`report-card-${r.id}`} onClick={() => nav(`/reports/${r.id}`)}
                className="bg-[#18181B] border border-zinc-800 hover:border-zinc-700 rounded-2xl p-4 transition-all cursor-pointer shadow-lg">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-lg font-extrabold text-white">{r.customer_name}</h3>
                  <div className="flex items-center gap-2 shrink-0"><StatusChip status={r.status} /><span className="text-xs text-zinc-500">{r.report_date || (r.created_at || "").slice(0, 10)}</span></div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {r.call_id && <span className="bg-zinc-800 text-zinc-300 text-[11px] px-2 py-1 rounded-md font-medium">{t("Call")} {r.call_id}</span>}
                  <span className="bg-zinc-800 text-zinc-300 text-[11px] px-2 py-1 rounded-md font-medium">{mc} {mc !== 1 ? t("machines") : t("machine")}</span>
                  <span className="bg-[#3B1D0E] text-[#F97316] text-[11px] px-2 py-1 rounded-md font-medium border border-[#F26722]/30 flex items-center gap-1"><Package size={12} /> {pc} {t("parts")}</span>
                </div>
                <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-zinc-800/80">
                  <span className="text-xs text-emerald-400 font-medium flex items-center gap-1.5"><Mail size={14} /> {t("Sent to")} {r.recipient_name || t("office")}</span>
                  <ChevronRight size={18} className="text-zinc-600" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
